import {
  Autocomplete,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  styled,
  useTheme,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
} from '@mui/material';
import React from 'react';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import Row from 'src/frontend-common/components/Row';
import { Formik } from 'formik';
import { CreateUserAdminPayload, UserDetail, UserRole } from 'src/dto/User';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { paths } from 'src/frontend-common/components/Router';
import { useNavigate } from 'react-router-dom';
import { CompanyDetail } from 'src/dto/Company';
import { useCreateUserAdminMutation, useUpdateUserAdminMutation } from 'src/redux/services/usersAdmin';
import { AlertBox, FormMessage } from 'src/components/AlertBox';
import { useGetCompaniesAdminQuery } from 'src/redux/services/companiesAdmin';
import 'react-phone-input-2/lib/material.css';
import { AsYouType } from 'libphonenumber-js';
import * as Yup from 'yup';
import { useInitialPrefix } from 'src/hooks';
import IOSSwitch from 'src/components/IosSwitch';
import { useGetLoginHistoryQuery } from 'src/redux/services/users';
import { WhiteContainer } from 'src/style/common';
import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import EmptyState from 'src/frontend-common/components/EmptyState';
import ButtonComponent from 'src/components/ButtonComponent';
import { getActiveCompany } from 'src/frontend-common/localstorage';
interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone_country_code: string | null;
  phone: string | null;
  company: { value: string; label: string } | null;
  role: UserRole;
  active: boolean;
}

interface RoleSelectItem {
  label: string;
  value: UserRole;
}

const SecondWhiteContainer = styled(WhiteContainer)(({ theme }) => ({
  marginTop: 0,
  boxShadow: '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
  padding: theme.spacing(0),
}));

const TableCustomSize = styled(Table)(() => ({
  '& th, td': {
    width: '40%',
    '&:last-of-type': {
      width: '20%',
    },
  },
}));

const roleSelectItems: RoleSelectItem[] = [
  {
    label: 'Admin',
    value: 'ROLE_ADMIN',
  },
  {
    label: 'Customer',
    value: 'ROLE_CUSTOMER',
  },
];

export interface UserFormProps {
  userData?: UserDetail;
}
const FirstBlockContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
}));

const ButtonsWrapper = styled(Row)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const FormWrapper = styled(Card)(() => ({
  width: '100%',
}));

const PhoneErrorWrapper = styled('div')(() => ({
  fontWeight: 400,
  fontSize: '0.75rem',
  color: '#F44336',
  lineHeight: 1.66,
  letterSpacing: '0.03333em',
  textAlign: 'left',
  marginTop: 3,
  marginRight: 14,
  marginBottom: 0,
  marginLeft: 14,
}));

export const UserForm: React.FC<UserFormProps> = ({ userData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [createUserAdmin, createdUserAdmin] = useCreateUserAdminMutation();
  const [updateUserAdmin, updatedUserAdmin] = useUpdateUserAdminMutation();
  const [errorMessages, setErrorMessages] = React.useState<FormMessage[]>([]);
  const [phone_country_code, setPhoneCountryCode] = React.useState<string | undefined>();
  const { data: loginUserInfoData } = useGetLoginHistoryQuery({
    username: userData?.email || '',
  });

  const [companySearch, setCompanySearch] = React.useState('');
  const { data: companies } = useGetCompaniesAdminQuery({
    start: 0,
    max_items: 100,
    search: companySearch,
  });

  const activeCompany = getActiveCompany();
  React.useEffect(() => {
    if (activeCompany) {
      // search for the first two letters of the active company name
      setCompanySearch(activeCompany.name.substring(0, 2));
    }
  }, []);

  const initialPrefix = useInitialPrefix();

  const getCountryCode = (input: string) => {
    // Set default country code to US if no real country code is specified
    const defaultCountryCode = input.substr(0, 1) !== '+' ? 'US' : null;
    let formatted = new AsYouType('US').input(input);
    let countryCode: string | undefined = '';
    let withoutCountryCode = formatted;

    if (defaultCountryCode === 'US') {
      countryCode = '+1';
      formatted = '+1 ' + formatted;
    } else {
      const parts = formatted.split(' ');
      countryCode = parts.length > 1 ? parts.shift() : '';
      withoutCountryCode = parts.join(' ');
    }

    return {
      formatted,
      withoutCountryCode,
      countryCode,
    };
  };

  React.useEffect(() => {
    if (userData?.phone) {
      setPhoneCountryCode(getCountryCode(userData.phone).countryCode);
    }
  }, []);

  const initialPhoneNumber = userData?.phone ? userData.phone.replace(`+${initialPrefix}`, '') : userData?.phone;

  const onCompaniesSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompanySearch(event.target.value);
  };

  const getRole = (roles: UserRole[]) => {
    let role = '';
    roles.forEach((r) => {
      if (r !== 'ROLE_USER') {
        role = r;
      }
    });
    return role;
  };

  const goBack = () => navigate(paths.users.path);

  const getCompanyById = (id: string): CompanyDetail | undefined => {
    if (companies) {
      return companies.items.find((c) => c.id === id);
    }
    return;
  };

  const createPayload = (values: UserFormValues): CreateUserAdminPayload | null => {
    if (!values.company) {
      return null;
    }
    const selectedCompany = getCompanyById(values.company.value);
    if (!selectedCompany) {
      return null;
    }
    return {
      email: values.email,
      roles: ['ROLE_USER', values.role],
      company: {
        id: selectedCompany.id,
        name: selectedCompany.name,
      },
      first_name: values.firstName,
      last_name: values.lastName,
      username: values.email,
      active: values.active || false,
      phone: values.phone?.includes('+') ? values.phone : `+${values.phone}`,
      phone_country_code: values.phone_country_code ? `+${values.phone_country_code}` : phone_country_code || undefined,
    };
  };

  const handleUpdate = (id: string, values: UserFormValues) => {
    const payload = createPayload(values);
    if (payload) {
      updateUserAdmin({ id, ...payload });
    }
  };

  const handleCreate = (values: UserFormValues) => {
    const payload = createPayload(values);
    if (payload) {
      createUserAdmin(payload);
    }
  };

  const handleErrorMessageDismiss = (i: number) => {
    const newErrorMessages = [...errorMessages];
    newErrorMessages.splice(i, 1);
    setErrorMessages(newErrorMessages);
  };

  const getCompanyInitialValue = () => {
    if (userData) {
      return { value: userData.company.id, label: userData.company.name };
    }
    if (activeCompany) {
      return { value: activeCompany.id, label: activeCompany.name };
    }

    return null;
  };

  const DisplayingErrorMessagesSchema = Yup.object().shape({
    firstName: Yup.string().required('This field is required'),
    lastName: Yup.string().required('This field is required'),
    email: Yup.string().email('Email is not valid').required('This field is required'),
    phone_prefix: Yup.string(),
    phone: Yup.string().required('This field is required'),
    company: Yup.object().required('This field is required'),
    role: Yup.string().required('This field is required'),
  });

  return (
    <DashboardPageLayout title={userData ? 'User' : 'Create User'}>
      <AlertBox messages={errorMessages} onMessageDismiss={handleErrorMessageDismiss} />

      <Grid container={true}>
        <FormWrapper>
          <Formik
            initialValues={{
              firstName: userData ? userData.first_name : '',
              lastName: userData ? userData.last_name : '',
              email: userData ? userData.email : '',
              phone_prefix: '',
              phone: userData ? initialPhoneNumber : null,
              company: getCompanyInitialValue(),
              role: userData ? getRole(userData.roles) : 'ROLE_CUSTOMER',
              active: userData ? userData.active : false,
            }}
            validateOnChange={true}
            validationSchema={DisplayingErrorMessagesSchema}
            enableReinitialize
            onSubmit={(values) => {
              const userFormData: UserFormValues = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                company: values.company,
                role: values.role,
                active: values.active || false,
                phone: values.phone || null,
                phone_country_code: values.phone ? values.phone_prefix : null,
              };
              setErrorMessages([]);
              if (userData) {
                handleUpdate(userData.id, userFormData as UserFormValues);
              } else {
                handleCreate(userFormData as UserFormValues);
              }
            }}
          >
            {(formikProps) => {
              const {
                errors: { firstName: firstNameError, lastName: lastNameError, email: emailError, company: companyError, role: roleError },
                touched: {
                  firstName: firstNameTouched,
                  lastName: lastNameTouched,
                  email: emailTouched,
                  company: companyTouched,
                  role: roleTouched,
                  phone: phoneTouched,
                },
              } = formikProps;

              return (
                <>
                  <CardContent>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        formikProps.handleSubmit();
                      }}
                    >
                      <Grid container={true} spacing={3}>
                        <Grid item={true} xs={12} sm={12} md={6}>
                          <TextField
                            variant="outlined"
                            fullWidth={true}
                            label="First Name *"
                            error={firstNameTouched && !!firstNameError}
                            helperText={firstNameTouched && firstNameError}
                            {...formikProps.getFieldProps('firstName')}
                          />
                        </Grid>

                        <Grid item={true} xs={12} sm={12} md={6}>
                          <TextField
                            variant="outlined"
                            fullWidth={true}
                            label="Last Name *"
                            error={lastNameTouched && !!lastNameError}
                            helperText={lastNameTouched && lastNameError}
                            {...formikProps.getFieldProps('lastName')}
                          />
                        </Grid>

                        <Grid item={true} xs={12} sm={12} md={6}>
                          <TextField
                            variant="outlined"
                            fullWidth={true}
                            label="Email *"
                            disabled={userData ? true : false}
                            error={emailTouched && !!emailError}
                            helperText={emailTouched && emailError}
                            {...formikProps.getFieldProps('email')}
                          />
                        </Grid>

                        <Grid item={true} xs={12} sm={12} md={6} data-testid={phone_country_code}>
                          <PhoneInput
                            dropdownStyle={{ position: 'fixed' }}
                            country={initialPrefix?.toLowerCase() || formikProps.getFieldProps('phone_prefix').value}
                            isValid={phoneTouched && !formikProps.errors['phone']}
                            enableSearch
                            value={formikProps.getFieldProps('phone').value}
                            onChange={(phone, obj: CountryData) => {
                              formikProps.setFieldValue('phone_prefix', obj.dialCode, true);

                              formikProps.setFieldValue('phone', phone, true);
                            }}
                            onBlur={() => {
                              formikProps.setFieldTouched('phone_prefix');
                              formikProps.validateField('phone_prefix');
                              formikProps.setFieldTouched('phone');
                              formikProps.validateField('phone');
                            }}
                            inputStyle={{ width: '100%' }}
                            specialLabel="Phone *"
                            inputProps={{
                              required: true,
                            }}
                          />
                          {phoneTouched && formikProps.errors['phone'] && <PhoneErrorWrapper>{formikProps.errors['phone']}</PhoneErrorWrapper>}
                        </Grid>

                        <Grid item={true} xs={12} sm={12} md={6}>
                          <Autocomplete
                            options={
                              companies
                                ? companies.items.map((c) => ({
                                    value: c.id,
                                    label: c.name,
                                  }))
                                : []
                            }
                            getOptionLabel={(o) => {
                              return o.label || '';
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={companyTouched && !!companyError}
                                helperText={companyTouched && companyError}
                                label="Company *"
                                variant="outlined"
                                onChange={onCompaniesSearchChange}
                              />
                            )}
                            {...formikProps.getFieldProps('company')}
                            onChange={(e, value: string | null) => {
                              formikProps.setFieldValue('company', value);
                            }}
                          />
                        </Grid>

                        <Grid item={true} xs={12} sm={12} md={6}>
                          <FormControl fullWidth={true}>
                            <InputLabel>Role *</InputLabel>
                            <Select
                              {...formikProps.getFieldProps('role')}
                              label="Role *"
                              fullWidth={true}
                              error={roleTouched ? !!roleError : false}
                              onChange={(v) => {
                                formikProps.setFieldTouched('phone', true);
                                formikProps.setFieldValue('role', v.target.value);
                              }}
                            >
                              {roleSelectItems.map((role) => (
                                <MenuItem key={role.label} value={role.value}>
                                  {role.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {roleError && <FormHelperText style={{ color: '#D14343' }}>{roleError}</FormHelperText>}
                          </FormControl>
                        </Grid>

                        {userData && (
                          <Grid item={true} xs={12}>
                            <Typography variant="h4">User status</Typography>

                            <Typography variant="body2" color="text.secondary">
                              Disabling this will prevent the user to access the dashboard.
                            </Typography>

                            <IOSSwitch
                              sx={{ marginTop: '10px' }}
                              color="primary"
                              checked={formikProps.getFieldProps('active').value}
                              {...formikProps.getFieldProps('active')}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </form>
                  </CardContent>
                  <ButtonsWrapper hAlign="flex-end">
                    <ButtonComponent
                      buttontype="cancel"
                      disabled={updatedUserAdmin.isLoading || createdUserAdmin.isLoading}
                      onClick={goBack}
                      sx={{ marginRight: theme.spacing(3) }}
                    />
                    <ButtonComponent
                      buttontype="mainaction"
                      text="Save"
                      data-testid="save-button"
                      disabled={updatedUserAdmin.isLoading || !(Object.keys(formikProps.errors).length === 0 && formikProps.dirty)}
                      onClick={() => formikProps.submitForm()}
                    />
                  </ButtonsWrapper>
                </>
              );
            }}
          </Formik>
        </FormWrapper>
      </Grid>
      <SecondWhiteContainer style={{ marginTop: 24 }}>
        <FirstBlockContainer>
          <Typography variant="h5">Login History</Typography>
        </FirstBlockContainer>
        {loginUserInfoData && loginUserInfoData?.logins?.length > 0 ? (
          <Grid item>
            <TableCustomSize>
              <TableHead>
                <TableRow>
                  <TableCell>LOGIN</TableCell>
                  <TableCell>IP ADDRESS</TableCell>
                  <TableCell>DATE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loginUserInfoData.logins.map((user, i) => (
                  <TableRow key={i}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.ip}</TableCell>
                    <TableCell>{user.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableCustomSize>
          </Grid>
        ) : (
          <EmptyState />
        )}
      </SecondWhiteContainer>
      <SnackBarCompleteHandler
        isSuccess={updatedUserAdmin.isSuccess}
        isError={updatedUserAdmin.isError}
        error={updatedUserAdmin.error}
        successMessage="User successfully updated"
      />
      <SnackBarCompleteHandler
        isSuccess={createdUserAdmin.isSuccess}
        isError={createdUserAdmin.isError}
        error={createdUserAdmin.error}
        successMessage="The user was correctly created! An email with the password has been sent"
      />
    </DashboardPageLayout>
  );
};
